/**
 * Kono operators only: change anything on a quote that is not an order yet,
 * line by line (item and vendor, quantity, price, currency, lead time,
 * description), add or remove lines, set the freight and fix the customer.
 * Saving creates the next revision; the version the customer may already
 * hold stays readable.
 *
 * A quote is a snapshot of the catalogue at the moment it was saved: a price
 * corrected afterwards (superpowers, a vendor's new list) does not reach the
 * quote by itself. The catalogue's price today is shown next to the quoted
 * one so the operator can take it with one click.
 */
import { useEffect, useMemo, useState } from "react";
import { CartItem } from "../common/models/cart";
import {
  CatalogOption,
  OperatorLineEdit,
  OperatorLineView,
  OperatorQuoteView,
  Quote,
  loadCatalogOptions,
  loadOperatorQuoteView,
  messageOf,
  saveOperatorRevision,
} from "./api";

const INPUT = "rounded border border-gray-300 px-2 py-1 text-sm";
const BUTTON = "rounded-full bg-[#00062e] px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50";
const BUTTON_LIGHT = "rounded-full border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50";

const priceText = (price: { value: number; unit: string; currency: string } | null | undefined): string =>
  price ? `${price.currency === "USD" ? "$" : `${price.currency} `}${price.value.toLocaleString("en-US", { maximumFractionDigits: 4 })}/${price.unit || "unit"}` : "no price";

const leadText = (lead: { min: number | null; max: number | null }): string =>
  lead.min !== null && lead.max !== null ? (lead.min === lead.max ? `${lead.min} days` : `${lead.min}-${lead.max} days`) : lead.max !== null ? `up to ${lead.max} days` : "";

type LineDraft = {
  index: number;
  remove: boolean;
  /** Take the catalogue item as it is now (price, size, description). */
  refresh: boolean;
  productId: string;
  productSummary: string;
  supplierName: string;
  quantityValue: string;
  quantityUnit: string;
  unitPrice: string;
  /** The final amount for the line; typed here, the unit price follows, and the other way round. */
  lineTotal: string;
  /** Which of the two the operator typed last; that one is sent. */
  priceSource: "unit" | "total";
  currency: string;
  /** Vendor cost to Kono, internal. */
  supplierPrice: string;
  leadTime: string;
  description: string;
};

type AddedDraft = {
  option: CatalogOption;
  quantityValue: string;
  quantityUnit: string;
  unitPrice: string;
  lineTotal: string;
  leadTime: string;
  description: string;
};

const draftOf = (item: CartItem, view: OperatorLineView | undefined, index: number): LineDraft => ({
  index,
  remove: false,
  refresh: false,
  productId: String(item.product?.id || ""),
  productSummary: String(item.product?.summary || ""),
  supplierName: view?.supplierName || "",
  quantityValue: item.quantityRequired ? String(item.quantityRequired.value) : "",
  quantityUnit: item.quantityRequired?.unit || "unit",
  unitPrice: view?.override?.unitPrice !== undefined ? String(view.override.unitPrice) : "",
  lineTotal: "",
  priceSource: "unit",
  currency: view?.override?.currency || item.product?.price?.currency || "USD",
  // The vendor cost starts at what is known: the operator's figure if any,
  // else the catalogue price (the vendor's own list), so it is never blank.
  supplierPrice:
    view?.override?.supplierPrice?.value !== undefined
      ? String(view.override.supplierPrice.value)
      : view?.catalogPrice?.value !== undefined
        ? String(view.catalogPrice.value)
        : item.product?.price?.value !== undefined
          ? String(item.product.price.value)
          : "",
  leadTime: view?.override?.leadTime || "",
  description: view?.override?.description || "",
});

const num = (value: string): number | null => (value.trim() === "" ? null : isFinite(Number(value)) && Number(value) > 0 ? Number(value) : null);

/**
 * How many pricing units (lb, ft, pieces) the line holds, from what the quote
 * already worked out (amount / unit price), scaled when the operator changes
 * the quantity in the same unit. Null when it cannot be known here; the
 * server then does the conversion from the total on save.
 */
const pricingUnitsOf = (line: Pick<LineDraft, "quantityValue" | "quantityUnit">, original: CartItem | undefined): number | null => {
  const amount = typeof original?.lineAmount === "number" ? original.lineAmount : null;
  const unitPrice = original?.product?.price?.value;
  if (!amount || !unitPrice || amount <= 0 || unitPrice <= 0) return null;
  const base = amount / unitPrice;
  const originalQuantity = original?.quantityRequired?.value || 0;
  const quantity = num(line.quantityValue);
  if (!quantity || !originalQuantity) return base;
  if ((line.quantityUnit || "").trim() !== (original?.quantityRequired?.unit || "").trim()) return null;
  return (base * quantity) / originalQuantity;
};

const trimNumber = (value: number, decimals: number): string => String(Math.round(value * 10 ** decimals) / 10 ** decimals);

/** Pick a catalogue item: for a line (same alloy and section) or by words (new line). */
const CatalogPicker = ({
  quoteId,
  line,
  onPick,
  onClose,
}: {
  quoteId: string;
  line: number;
  onPick: (option: CatalogOption) => void;
  onClose: () => void;
}) => {
  const [q, setQ] = useState("");
  const [options, setOptions] = useState<CatalogOption[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const search = async (words: string) => {
    setBusy(true);
    setError("");
    try {
      setOptions(await loadCatalogOptions(quoteId, line, words));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (line >= 0) void search("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line]);
  return (
    <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <input
          className={`${INPUT} w-72`}
          placeholder={line >= 0 ? "Narrow by vendor or words: savage, 144 in..." : "Find an item: inconel 625 round bar 2.5"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void search(q);
          }}
        />
        <button type="button" className={BUTTON_LIGHT} disabled={busy || (line < 0 && !q.trim())} onClick={() => void search(q)}>
          {busy ? "Searching..." : "Search the catalogue"}
        </button>
        <button type="button" className={BUTTON_LIGHT} onClick={onClose}>
          Close
        </button>
        <span className="text-xs text-gray-600">{line >= 0 ? "Same alloy, shape and section, every vendor." : "Any item in the catalogue."}</span>
      </div>
      {error && <p className="mt-2 text-red-600">{error}</p>}
      {options && options.length === 0 && !busy && <p className="mt-2 text-gray-600">Nothing in the catalogue matches.</p>}
      {options && options.length > 0 && (
        <table className="mt-2 w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 pr-2">Vendor</th>
              <th className="py-1 pr-2">Item</th>
              <th className="py-1 pr-2">Price</th>
              <th className="py-1 pr-2">Stock</th>
              <th className="py-1 pr-2">Lead time</th>
              <th className="py-1 pr-2">Where</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {options.map((option) => (
              <tr key={option.productId} className={`border-t border-blue-100 ${option.current ? "bg-white/60" : ""}`}>
                <td className="py-1 pr-2 font-medium">{option.supplierName}</td>
                <td className="py-1 pr-2">{option.summary}</td>
                <td className="py-1 pr-2 whitespace-nowrap">{priceText(option.price)}</td>
                <td className="py-1 pr-2 whitespace-nowrap">{option.quantityAvailable ? `${option.quantityAvailable.value} ${option.quantityAvailable.unit}` : ""}</td>
                <td className="py-1 pr-2 whitespace-nowrap">{leadText(option.leadTimeDays)}</td>
                <td className="py-1 pr-2">{option.location}</td>
                <td className="py-1 text-right">
                  {option.current ? (
                    <span className="text-gray-500">on the quote</span>
                  ) : (
                    <button type="button" className={BUTTON_LIGHT} onClick={() => onPick(option)}>
                      {line >= 0 ? "Use this item" : "Add"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const addressLine = (a: { streetLines?: string[]; city?: string; stateOrProvinceCode?: string; postalCode?: string; countryCode?: string } | null | undefined): string =>
  a ? [(a.streetLines || []).join(", "), a.city, a.stateOrProvinceCode, a.postalCode, a.countryCode && a.countryCode !== "US" ? a.countryCode : ""].filter(Boolean).join(", ") : "";

export const OperatorQuoteEditor = ({
  quote,
  onSaved,
  onClose,
  onOpenFreight,
}: {
  quote: Quote;
  onSaved: (quote: Quote, changes: string[], revised: boolean) => void;
  /** Opens the freight panel (addresses are corrected there). */
  onOpenFreight?: () => void;
  onClose: () => void;
}) => {
  const [view, setView] = useState<OperatorQuoteView | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [added, setAdded] = useState<AddedDraft[]>([]);
  const [picker, setPicker] = useState<number | "new" | null>(null);
  const [shippingMode, setShippingMode] = useState<"keep" | "set" | "clear">("keep");
  const [shippingAmount, setShippingAmount] = useState("");
  const [shippingLabel, setShippingLabel] = useState("");
  const [customer, setCustomer] = useState({ ...quote.customer });
  const [note, setNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    setError("");
    loadOperatorQuoteView(quote.id)
      .then((loaded) => {
        if (cancelled) return;
        setView(loaded);
        setLines(quote.cartItems.map((item, index) => draftOf(item, loaded.lines[index], index)));
        const shipping = loaded.shipping;
        if (shipping) {
          setShippingAmount(String(shipping.totalCharges?.monetaryValue ?? ""));
          setShippingLabel(shipping.displayLabel || "");
        }
      })
      .catch((e) => !cancelled && setError(messageOf(e)));
    return () => {
      cancelled = true;
    };
  }, [quote]);

  const setLine = (index: number, patch: Partial<LineDraft>) => setLines((all) => all.map((line) => (line.index === index ? { ...line, ...patch } : line)));

  const changes = useMemo(() => {
    const edits: OperatorLineEdit[] = [];
    lines.forEach((line) => {
      const original = quote.cartItems[line.index];
      const before = view?.lines[line.index];
      const edit: OperatorLineEdit = { index: line.index };
      let touched = false;
      if (line.remove) {
        edits.push({ index: line.index, remove: true });
        return;
      }
      if (line.productId && line.productId !== String(original?.product?.id || "")) {
        edit.productId = line.productId;
        touched = true;
      } else if (line.refresh) {
        edit.refresh = true;
        touched = true;
      }
      const quantity = num(line.quantityValue);
      if (quantity && (quantity !== original?.quantityRequired?.value || line.quantityUnit !== (original?.quantityRequired?.unit || ""))) {
        edit.quantity = { value: quantity, unit: line.quantityUnit };
        touched = true;
      }
      const price = num(line.unitPrice);
      const beforePrice = before?.override?.unitPrice ?? null;
      if (line.priceSource === "total" && num(line.lineTotal)) {
        // The operator typed the final amount: the server spreads it over the
        // line's weight or pieces so the quote prints exactly that figure.
        edit.lineTotal = num(line.lineTotal);
        touched = true;
      } else if (price !== beforePrice) {
        edit.unitPrice = price;
        touched = true;
      }
      const beforeCurrency = before?.override?.currency || original?.product?.price?.currency || "USD";
      if (line.currency.trim().toUpperCase() !== beforeCurrency) {
        edit.currency = line.currency.trim().toUpperCase();
        touched = true;
      }
      const supplierPrice = num(line.supplierPrice);
      const storedSupplier = before?.override?.supplierPrice?.value ?? null;
      const defaultSupplier = before?.catalogPrice?.value ?? original?.product?.price?.value ?? null;
      // Only a figure that differs from what is stored (or, with nothing
      // stored, from the catalogue price it was pre-filled with) is an edit.
      if (storedSupplier !== null ? supplierPrice !== storedSupplier : supplierPrice !== null && supplierPrice !== defaultSupplier) {
        edit.supplierPrice = supplierPrice;
        touched = true;
      }
      if (line.leadTime.trim() !== (before?.override?.leadTime || "")) {
        edit.leadTime = line.leadTime.trim();
        touched = true;
      }
      if (line.description.trim() !== (before?.override?.description || "")) {
        edit.description = line.description.trim();
        touched = true;
      }
      if (touched) edits.push(edit);
    });
    const customerPatch: Record<string, string> = {};
    (["name", "company", "email", "phone"] as const).forEach((key) => {
      if ((customer[key] || "").trim() !== (quote.customer[key] || "").trim()) customerPatch[key] = customer[key].trim();
    });
    return {
      lines: edits,
      added: added.map((entry) => ({
        productId: entry.option.productId,
        quantity: { value: num(entry.quantityValue) || 0, unit: entry.quantityUnit },
        unitPrice: num(entry.lineTotal) ? null : num(entry.unitPrice),
        lineTotal: num(entry.lineTotal),
        leadTime: entry.leadTime.trim() || null,
        description: entry.description.trim() || null,
      })),
      shipping: shippingMode === "set" ? { amount: Number(shippingAmount), label: shippingLabel.trim() || null } : shippingMode === "clear" ? { clear: true as const } : undefined,
      customer: Object.keys(customerPatch).length ? customerPatch : undefined,
      note: note.trim() || null,
    };
  }, [lines, added, shippingMode, shippingAmount, shippingLabel, customer, note, quote, view]);

  const anything = changes.lines.length > 0 || changes.added.length > 0 || changes.shipping !== undefined || changes.customer !== undefined;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      if (changes.added.some((entry) => !entry.quantity.value)) throw new Error("Every added item needs a quantity.");
      if (shippingMode === "set" && (shippingAmount.trim() === "" || !isFinite(Number(shippingAmount)) || Number(shippingAmount) < 0)) throw new Error("Enter the freight amount (0 for free).");
      const result = await saveOperatorRevision(quote.id, changes);
      onSaved(result.quote, result.changes, result.revised !== false);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  const currency = quote.totals?.currency || "USD";

  return (
    <section className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-[#00062e]">Edit this quote (Kono)</h2>
        <button type="button" className={BUTTON_LIGHT} onClick={onClose}>
          Close without saving
        </button>
      </div>
      <p className="mt-1 text-xs text-gray-700">
        {view?.sentToCustomer
          ? `The customer holds ${quote.reference}${view.sentToCustomerAt ? ` (sent ${new Date(view.sentToCustomerAt).toLocaleDateString()})` : ""}: saving creates the next letter, priced again, and the customer keeps what they received until you send them the new one.`
          : `${quote.reference} has not been sent to the customer yet: save as often as you like, every change lands in this same version. Once you email it to the customer or press "Mark as sent to customer", changes make a new version.`}
        {" "}The quote is a snapshot: an item edited on the inventory page after the quote was saved (price, length, description) shows
        here as "catalogue now" and is taken only when you press "use it". The vendor cost is for Kono only; the customer price is independent of it.
      </p>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {!view && !error && <p className="mt-2 text-sm text-gray-500">Loading the vendors and catalogue prices...</p>}
      {view && !view.editable && <p className="mt-2 text-sm text-red-700">This quote cannot change any more (it became an order or was replaced by a newer version).</p>}

      {view && view.editable && (
        <>
          <div className="mt-3 space-y-3">
            {lines.map((line) => {
              const info = view.lines[line.index];
              const original = quote.cartItems[line.index];
              const quoted = original?.product?.price;
              const catalog = info?.catalogPrice;
              const catalogDiffers = catalog && quoted && (Math.abs(catalog.value - quoted.value) > 0.0001 || catalog.unit !== quoted.unit || catalog.currency !== quoted.currency);
              const catalogSummaryDiffers = Boolean(info?.inCatalog && info.catalogSummary && info.catalogSummary !== String(original?.product?.summary || ""));
              const sameItem = line.productId === String(original?.product?.id || "");
              return (
                <div key={line.index} className={`rounded-lg border bg-white p-3 ${line.remove ? "border-red-200 opacity-60" : "border-gray-200"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="text-sm">
                      <span className="text-xs text-gray-500">Line {line.index + 1}</span>
                      <div className="font-medium text-[#00062e]">{line.productSummary || "Item"}</div>
                      <div className="text-xs text-gray-600">
                        Vendor: <span className="font-medium">{line.supplierName || "unknown"}</span>
                        {line.productId !== String(original?.product?.id || "") ? <span className="ml-1 rounded bg-amber-100 px-1 text-amber-900">changed, was {info?.supplierName || "another vendor"}</span> : ""}
                        {" · "}quoted at {priceText(quoted)}
                        {info?.override?.unitPrice !== undefined ? ` (operator price ${info.override.unitPrice})` : ""}
                        {info && !info.inCatalog ? " · no longer in the catalogue" : ""}
                        {sameItem && (catalogDiffers || catalogSummaryDiffers) ? (
                          <>
                            {" · "}
                            <span className="text-amber-900">
                              catalogue now {catalogSummaryDiffers ? `${info!.catalogSummary} at ` : ""}
                              {priceText(catalog)}
                            </span>{" "}
                            {line.refresh ? (
                              <>
                                <span className="rounded bg-emerald-100 px-1 text-emerald-900">will be used on save</span>{" "}
                                <button type="button" className="text-blue-700 underline" onClick={() => setLine(line.index, { refresh: false })}>
                                  keep as quoted
                                </button>
                              </>
                            ) : (
                              <button type="button" className="text-blue-700 underline" onClick={() => setLine(line.index, { refresh: true, unitPrice: "", currency: catalog?.currency || line.currency })}>
                                use it
                              </button>
                            )}
                          </>
                        ) : (
                          ""
                        )}
                        {info?.quantityAvailable ? ` · stock ${info.quantityAvailable.value} ${info.quantityAvailable.unit}` : ""}
                        {leadText(info?.leadTimeDays || { min: null, max: null }) ? ` · vendor lead time ${leadText(info!.leadTimeDays)}` : ""}
                      </div>
                      {info && (
                        <div className="mt-1 text-xs text-gray-600">
                          Ships from:{" "}
                          <span className={info.shipFrom ? "text-gray-800" : "text-red-700"}>
                            {addressLine(info.shipFrom) || "unknown (no vendor address on file)"}
                          </span>
                          {info.shipFromSource === "warehouse" ? " (warehouse)" : info.shipFromSource === "operator" ? " (corrected by an operator)" : ""}
                          {" · "}Delivers to:{" "}
                          <span className={info.shipTo ? "text-gray-800" : "text-red-700"}>{addressLine(info.shipTo) || "none on this line"}</span>
                          {onOpenFreight ? (
                            <>
                              {" "}
                              <button type="button" className="text-blue-700 underline" onClick={onOpenFreight}>
                                Correct addresses / freight cost
                              </button>
                            </>
                          ) : null}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button type="button" className={BUTTON_LIGHT} disabled={line.remove} onClick={() => setPicker(picker === line.index ? null : line.index)}>
                        Change item / vendor
                      </button>
                      <button type="button" className={BUTTON_LIGHT} onClick={() => setLine(line.index, { remove: !line.remove })}>
                        {line.remove ? "Keep the line" : "Remove"}
                      </button>
                    </div>
                  </div>
                  {picker === line.index && (
                    <CatalogPicker
                      quoteId={quote.id}
                      line={line.index}
                      onClose={() => setPicker(null)}
                      onPick={(option) => {
                        setLine(line.index, { productId: option.productId, productSummary: option.summary, supplierName: option.supplierName, unitPrice: "", currency: option.price?.currency || line.currency });
                        setPicker(null);
                      }}
                    />
                  )}
                  {!line.remove && (
                    <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      <label className="text-xs text-gray-600">
                        Quantity
                        <div className="flex gap-1">
                          <input
                            className={`${INPUT} w-24`}
                            value={line.quantityValue}
                            onChange={(e) => {
                              // A new quantity moves whichever figure the operator did not type.
                              const next = { ...line, quantityValue: e.target.value };
                              const units = pricingUnitsOf(next, original);
                              const price = num(line.unitPrice);
                              const total = num(line.lineTotal);
                              setLine(line.index, {
                                quantityValue: e.target.value,
                                ...(line.priceSource === "total" && total ? { unitPrice: units ? trimNumber(total / units, 4) : line.unitPrice } : price ? { lineTotal: units ? trimNumber(price * units, 2) : "" } : {}),
                              });
                            }}
                            inputMode="decimal"
                          />
                          <input className={`${INPUT} w-16`} value={line.quantityUnit} onChange={(e) => setLine(line.index, { quantityUnit: e.target.value })} />
                        </div>
                      </label>
                      <label className="text-xs text-gray-600">
                        Price per {quoted?.unit || "unit"} (blank = catalogue)
                        <div className="flex gap-1">
                          <input
                            className={`${INPUT} w-24`}
                            value={line.unitPrice}
                            placeholder={quoted ? String(quoted.value) : ""}
                            onChange={(e) => {
                              const units = pricingUnitsOf(line, original);
                              const price = num(e.target.value);
                              setLine(line.index, { unitPrice: e.target.value, priceSource: "unit", lineTotal: units && price ? trimNumber(price * units, 2) : "" });
                            }}
                            inputMode="decimal"
                          />
                          <input className={`${INPUT} w-16 uppercase`} value={line.currency} maxLength={3} onChange={(e) => setLine(line.index, { currency: e.target.value })} />
                        </div>
                      </label>
                      <label className="text-xs text-gray-600">
                        Line total (or type this; the unit price follows)
                        <input
                          className={`${INPUT} w-28`}
                          value={line.lineTotal}
                          placeholder={typeof original?.lineAmount === "number" ? trimNumber(original.lineAmount, 2) : ""}
                          onChange={(e) => {
                            const units = pricingUnitsOf(line, original);
                            const total = num(e.target.value);
                            setLine(line.index, { lineTotal: e.target.value, priceSource: "total", unitPrice: units && total ? trimNumber(total / units, 4) : line.unitPrice });
                          }}
                          inputMode="decimal"
                        />
                        {line.priceSource === "total" && num(line.lineTotal) && !pricingUnitsOf(line, original) ? <span className="block text-amber-900">Unit price worked out on save.</span> : null}
                      </label>
                      <label className="text-xs text-gray-600">
                        Vendor cost per {quoted?.unit || "unit"} (internal; catalogue price unless you change it)
                        <input className={`${INPUT} w-24`} value={line.supplierPrice} placeholder="what the vendor charges" onChange={(e) => setLine(line.index, { supplierPrice: e.target.value })} inputMode="decimal" />
                      </label>
                      <label className="text-xs text-gray-600">
                        Lead time (printed on the quote)
                        <input className={`${INPUT} w-full`} value={line.leadTime} placeholder="e.g. 3-5 business days" onChange={(e) => setLine(line.index, { leadTime: e.target.value })} />
                      </label>
                      <label className="text-xs text-gray-600">
                        Description (blank = catalogue)
                        <input className={`${INPUT} w-full`} value={line.description} placeholder={line.productSummary} onChange={(e) => setLine(line.index, { description: e.target.value })} />
                      </label>
                    </div>
                  )}
                </div>
              );
            })}

            {added.map((entry, position) => (
              <div key={`${entry.option.productId}-${position}`} className="rounded-lg border border-emerald-200 bg-white p-3">
                <div className="flex flex-wrap items-start justify-between gap-2 text-sm">
                  <div>
                    <span className="text-xs text-emerald-700">New line</span>
                    <div className="font-medium text-[#00062e]">{entry.option.summary}</div>
                    <div className="text-xs text-gray-600">
                      Vendor: <span className="font-medium">{entry.option.supplierName}</span> · catalogue {priceText(entry.option.price)}
                      {entry.option.quantityAvailable ? ` · stock ${entry.option.quantityAvailable.value} ${entry.option.quantityAvailable.unit}` : ""}
                    </div>
                  </div>
                  <button type="button" className={BUTTON_LIGHT} onClick={() => setAdded((all) => all.filter((_, i) => i !== position))}>
                    Remove
                  </button>
                </div>
                <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  <label className="text-xs text-gray-600">
                    Quantity
                    <div className="flex gap-1">
                      <input className={`${INPUT} w-24`} value={entry.quantityValue} onChange={(e) => setAdded((all) => all.map((a, i) => (i === position ? { ...a, quantityValue: e.target.value } : a)))} inputMode="decimal" />
                      <input className={`${INPUT} w-16`} value={entry.quantityUnit} onChange={(e) => setAdded((all) => all.map((a, i) => (i === position ? { ...a, quantityUnit: e.target.value } : a)))} />
                    </div>
                  </label>
                  <label className="text-xs text-gray-600">
                    Price per {entry.option.price?.unit || "unit"} (blank = catalogue)
                    <input className={`${INPUT} w-full`} value={entry.unitPrice} onChange={(e) => setAdded((all) => all.map((a, i) => (i === position ? { ...a, unitPrice: e.target.value, lineTotal: "" } : a)))} inputMode="decimal" />
                  </label>
                  <label className="text-xs text-gray-600">
                    Line total (instead of a unit price)
                    <input className={`${INPUT} w-full`} value={entry.lineTotal} onChange={(e) => setAdded((all) => all.map((a, i) => (i === position ? { ...a, lineTotal: e.target.value, unitPrice: "" } : a)))} inputMode="decimal" />
                  </label>
                  <label className="text-xs text-gray-600">
                    Lead time
                    <input className={`${INPUT} w-full`} value={entry.leadTime} onChange={(e) => setAdded((all) => all.map((a, i) => (i === position ? { ...a, leadTime: e.target.value } : a)))} />
                  </label>
                  <label className="text-xs text-gray-600">
                    Description
                    <input className={`${INPUT} w-full`} value={entry.description} onChange={(e) => setAdded((all) => all.map((a, i) => (i === position ? { ...a, description: e.target.value } : a)))} />
                  </label>
                </div>
              </div>
            ))}

            <div>
              <button type="button" className={BUTTON_LIGHT} onClick={() => setPicker(picker === "new" ? null : "new")}>
                + Add an item from the catalogue
              </button>
              {picker === "new" && (
                <CatalogPicker
                  quoteId={quote.id}
                  line={-1}
                  onClose={() => setPicker(null)}
                  onPick={(option) => {
                    setAdded((all) => [...all, { option, quantityValue: "1", quantityUnit: option.quantityAvailable?.unit && !/lb|kg/i.test(option.quantityAvailable.unit) ? option.quantityAvailable.unit : "unit", unitPrice: "", lineTotal: "", leadTime: "", description: "" }]);
                    setPicker(null);
                  }}
                />
              )}
            </div>
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
              <div className="font-medium text-[#00062e]">Freight on the quote</div>
              <div className="mt-1 text-xs text-gray-600">
                Now: {quote.totals.shippingTotal === 0 ? "free / none" : `${currency} ${quote.totals.shippingTotal.toFixed(2)}`}
                {view.shipping ? ` (set by an operator: ${view.shipping.displayLabel || "freight"})` : " (as the cart computed it)"}. Rate the real move in Internal &gt; Sales &gt; Freight Cost Tool and put the amount here.
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                <label className="flex items-center gap-1">
                  <input type="radio" checked={shippingMode === "keep"} onChange={() => setShippingMode("keep")} /> keep as is
                </label>
                <label className="flex items-center gap-1">
                  <input type="radio" checked={shippingMode === "set"} onChange={() => setShippingMode("set")} /> charge
                </label>
                <input className={`${INPUT} w-24`} disabled={shippingMode !== "set"} value={shippingAmount} placeholder="0.00" onChange={(e) => setShippingAmount(e.target.value)} inputMode="decimal" />
                <input className={`${INPUT} w-56`} disabled={shippingMode !== "set"} value={shippingLabel} placeholder="label, e.g. WWEX LTL from Simi Valley" onChange={(e) => setShippingLabel(e.target.value)} />
                {view.shipping && (
                  <label className="flex items-center gap-1">
                    <input type="radio" checked={shippingMode === "clear"} onChange={() => setShippingMode("clear")} /> back to the cart's freight
                  </label>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
              <div className="font-medium text-[#00062e]">Customer on the quote</div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {(["name", "company", "email", "phone"] as const).map((key) => (
                  <label key={key} className="text-xs capitalize text-gray-600">
                    {key}
                    <input className={`${INPUT} w-full`} value={customer[key] || ""} onChange={(e) => setCustomer({ ...customer, [key]: e.target.value })} />
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input className={`${INPUT} w-96`} value={note} placeholder="Note for the trail (why this changed), optional" onChange={(e) => setNote(e.target.value)} />
            <button type="button" className={BUTTON} disabled={saving || !anything} onClick={() => void save()}>
              {saving ? "Saving..." : view?.sentToCustomer ? "Save as a new version" : `Save changes to ${quote.reference}`}
            </button>
            {!anything && <span className="text-xs text-gray-500">Nothing changed yet.</span>}
          </div>
        </>
      )}
    </section>
  );
};
